import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { QrCode, ArrowRight } from "@phosphor-icons/react";

const initial = {
  part_name: "",
  part_number: "",
  batch_number: "",
  quantity: 40,
  scans_allowed: 0,
  expire_page_link: "",
};

export default function Generate() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const navigate = useNavigate();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        quantity: parseInt(form.quantity),
        scans_allowed: parseInt(form.scans_allowed),
        expire_page_link: form.expire_page_link?.trim() || null,
      };
      const { data } = await api.post("/admin/qr/generate", payload);
      toast.success(`${data.count} QR codes generated`);
      setResult(data);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Generation failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="generate-root">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">QR Issuance</div>
        <h1 className="mt-1 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
          Generate QR batch
        </h1>
        <p className="mt-1 text-sm text-slate-600 max-w-2xl">
          Each QR receives a unique encrypted token, is stored in the database, and resolves to a customer-facing
          verification page upon scan.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <form onSubmit={submit} className="lg:col-span-2 rounded-lg border border-slate-200 bg-white p-6 space-y-5">
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Part Name" testId="form-part-name">
              <Input value={form.part_name} onChange={set("part_name")} required placeholder="e.g. Spark Plug B7HS" data-testid="input-part-name" className="h-11" />
            </Field>
            <Field label="Part Number" testId="form-part-number">
              <Input value={form.part_number} onChange={set("part_number")} required placeholder="e.g. SP-B7HS-001" data-testid="input-part-number" className="h-11" />
            </Field>
            <Field label="Batch Number" testId="form-batch">
              <Input value={form.batch_number} onChange={set("batch_number")} required placeholder="e.g. BATCH-2026-01" data-testid="input-batch-number" className="h-11" />
            </Field>
            <Field label="Quantity of QR codes" testId="form-quantity">
              <Input type="number" min="1" max="2000" value={form.quantity} onChange={set("quantity")} required data-testid="input-quantity" className="h-11" />
            </Field>
            <Field label="Scans Allowed (per QR)" testId="form-scans-allowed">
              <Input
                type="number"
                min="0"
                max="100"
                value={form.scans_allowed}
                onChange={set("scans_allowed")}
                required
                placeholder="0 = unlimited"
                data-testid="input-scans-allowed"
                className="h-11"
              />
              <p className="mt-1 text-[11px] text-slate-500">
                Max verifications per QR before the limit page appears. Set <span className="font-semibold">0</span> for unlimited scans.
              </p>
            </Field>
            <Field label="Expire Page Link" testId="form-expire-page-link">
              <Input
                type="url"
                value={form.expire_page_link}
                onChange={set("expire_page_link")}
                placeholder="https://example.com/expired"
                data-testid="input-expire-page-link"
                className="h-11"
              />
              <p className="mt-1 text-[11px] text-slate-500">
                URL to redirect to (in the same tab) once a QR exceeds its scan limit. Leave empty to keep the default "Genuine Check is already done" page.
              </p>
            </Field>
          </div>

          <div className="pt-2 flex items-center gap-3">
            <Button
              type="submit"
              disabled={submitting}
              className="bg-[#253c80] hover:bg-[#1a2a5e] text-white font-semibold h-11 px-6"
              data-testid="generate-submit-button"
            >
              <QrCode size={18} weight="bold" className="mr-1.5" />
              {submitting ? "Generating…" : "Generate QR"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setForm(initial)}
              className="h-11"
              data-testid="generate-reset-button"
            >
              Reset
            </Button>
          </div>
        </form>

        <aside className="rounded-lg border border-slate-200 bg-gradient-to-br from-[#253c80] to-[#1a2a5e] text-white p-6 space-y-3">
          <div className="h-10 w-10 rounded-md bg-white/15 grid place-items-center ring-1 ring-white/20">
            <QrCode size={22} weight="duotone" />
          </div>
          <h3 className="text-xl font-extrabold tracking-tight">How it works</h3>
          <ul className="space-y-2 text-sm text-white/85">
            <li>• Each QR gets a unique 11-character encrypted token.</li>
            <li>• Codes are persisted with <span className="font-semibold">status = unused</span>.</li>
            <li>• Scanning marks them <span className="font-semibold">verified</span> and locks the record.</li>
            <li>• Duplicate scans are tracked and surfaced as tamper signals.</li>
          </ul>
        </aside>
      </div>

      {result && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-6" data-testid="generate-result">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700">Batch ready</div>
              <h3 className="mt-1 text-lg font-bold text-slate-900">
                {result.count} QR codes generated
              </h3>
              <p className="text-sm text-slate-600">
                Batch ID: <span className="font-mono font-semibold">{result.batch_id}</span>
              </p>
            </div>
            <Button
              onClick={() => navigate(`/print?batch=${result.batch_id}`)}
              className="bg-[#DC4226] hover:bg-[#b3351e] text-white font-semibold"
              data-testid="generate-go-print"
            >
              Print labels <ArrowRight size={16} weight="bold" className="ml-1" />
            </Button>
          </div>
          <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2">
            {result.codes.slice(0, 18).map((c) => (
              <code key={c} className="block text-[11px] bg-white rounded px-2 py-1 border border-slate-200 truncate">
                {c}
              </code>
            ))}
            {result.codes.length > 18 && (
              <span className="text-xs text-slate-500 self-center">+{result.codes.length - 18} more…</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children, testId }) {
  return (
    <div data-testid={testId}>
      <Label className="text-sm font-medium text-slate-700">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
